import { forwardRef, useMemo } from "react";
import { type Column } from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import { CollectionReportRow, useCollectionReportRows } from "../model/use-collection-report-rows";
import { useReportHyperlinks } from "../model/use-report-hyperlinks";
import {
  amountCell,
  byNumber,
  compareIds,
  entityNumber,
  numberColumn,
  reportDate,
  sumOf,
  textCell,
} from "../model/job-report";
import { ReportTable, ReportTableHandle, ReportTableProps } from "./report-table";

/** A report row, or the totals line that closes the exports. */
type CollectionExportRow = CollectionReportRow & { isTotal?: boolean };

/** «Детальный отчёт по вывозу» (AIS 15.1.2): each completed job with its crew and contract. */
export const CollectionReportTable = forwardRef<ReportTableHandle, ReportTableProps>(
  (props, ref) => {
    const t = useT();
    const { rows, isLoading, error } = useCollectionReportRows(props.startDate, props.endDate);
    const { locationCell, customerCell, driverCell, plateCell, entityCards } =
      useReportHyperlinks();

    const columns: Column<CollectionExportRow>[] = useMemo(() => {
      return [
        numberColumn(
          t(Namespace.Analytics, "columns.number"),
          t(Namespace.Analytics, "collection.totals.label"),
        ),
        {
          id: "id",
          header: t(Namespace.Analytics, "columns.id"),
          accessor: (row) => (row.isTotal ? "" : entityNumber("job", row.jobId)),
          width: 100,
          sortable: true,
          sortFn: (a, b) => compareIds(a.jobId, b.jobId),
        },
        {
          id: "collectedOn",
          header: t(Namespace.Analytics, "columns.exportDate"),
          accessor: (row) => (row.isTotal ? "" : reportDate(row.factEndAt)),
          width: 120,
          sortable: true,
          sortFn: byNumber((row) => row.factEndAt?.getTime()),
        },
        {
          id: "client",
          header: t(Namespace.Analytics, "table.customer"),
          key: "serviceReceiverName",
          width: 200,
          sortable: true,
          cell: (info) =>
            customerCell(info.row.original.serviceReceiverId, info.getValue() as string),
        },
        {
          id: "bin",
          header: t(Namespace.Analytics, "columns.bin"),
          accessor: (row) => textCell(row.serviceReceiverBin, row.isTotal),
          width: 130,
          sortable: true,
        },
        {
          id: "site",
          header: t(Namespace.Analytics, "columns.site"),
          key: "pickupLocationName",
          width: 200,
          sortable: true,
          cell: (info) =>
            locationCell(info.row.original.pickupLocationId, info.getValue() as string),
        },
        {
          id: "containerType",
          header: t(Namespace.Analytics, "collection.containerType"),
          accessor: (row) => textCell(row.containerTypeName, row.isTotal),
          width: 160,
          sortable: true,
        },
        {
          id: "count",
          header: t(Namespace.Analytics, "collection.count"),
          accessor: (row) => amountCell(row.numberOfContainers, 0, row.isTotal),
          width: 120,
          sortable: true,
          sortFn: byNumber((row) => row.numberOfContainers),
        },
        {
          id: "volume",
          header: t(Namespace.Analytics, "columns.nominalVolume"),
          accessor: (row) => amountCell(row.containerVolume, 2, row.isTotal),
          width: 130,
          sortable: true,
          sortFn: byNumber((row) => row.containerVolume),
        },
        {
          id: "factVolume",
          header: t(Namespace.Analytics, "columns.factVolume"),
          accessor: (row) => amountCell(row.factTotalVolume, 2, row.isTotal),
          width: 130,
          sortable: true,
          sortFn: byNumber((row) => row.factTotalVolume),
        },
        {
          id: "truck",
          header: t(Namespace.Analytics, "collection.truck"),
          key: "truckPlateNumber",
          width: 120,
          sortable: true,
          cell: (info) => plateCell(info.row.original.truckId, info.getValue() as string),
        },
        {
          id: "driver",
          header: t(Namespace.Analytics, "collection.driver"),
          key: "driverName",
          width: 170,
          sortable: true,
          cell: (info) => driverCell(info.row.original.driverId, info.getValue() as string),
        },
        {
          id: "loader",
          header: t(Namespace.Analytics, "collection.loader"),
          accessor: (row) => textCell(row.loaderNames, row.isTotal),
          width: 170,
          sortable: true,
        },
        {
          id: "contract",
          header: t(Namespace.Analytics, "collection.contract"),
          accessor: (row) => textCell(row.contractDealNumber, row.isTotal),
          width: 140,
          sortable: true,
        },
      ];
    }, [t, locationCell, customerCell, driverCell, plateCell]);

    const exportRows = useMemo<CollectionExportRow[]>(
      () =>
        rows.length
          ? [
              ...rows,
              {
                isTotal: true,
                jobId: 0n,
                factEndAt: null,
                serviceReceiverId: null,
                serviceReceiverName: "",
                serviceReceiverBin: "",
                pickupLocationId: null,
                pickupLocationName: "",
                containerTypeId: "",
                containerTypeName: "",
                numberOfContainers: sumOf(rows.map((r) => r.numberOfContainers)),
                containerVolume: sumOf(rows.map((r) => r.containerVolume)),
                factTotalVolume: sumOf(rows.map((r) => r.factTotalVolume)),
                truckId: null,
                truckPlateNumber: "",
                driverId: null,
                driverName: "",
                loaderNames: "",
                contractDealNumber: "",
              },
            ]
          : rows,
      [rows],
    );

    return (
      <ReportTable
        {...props}
        exportRef={ref}
        settingsKey="collection_report"
        reportName={t(Namespace.Analytics, "reports.registry")}
        columns={columns}
        rows={rows}
        exportRows={exportRows}
        rowKey={(row) => String(row.jobId)}
        isLoading={isLoading}
        error={error}
      >
        {entityCards}
      </ReportTable>
    );
  },
);
